import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Email as EmailIcon,
  PictureAsPdf as PictureAsPdfIcon,
  UploadFile as UploadFileIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import ManagerReportsSection from '../../components/manager/ManagerReportsSection';
import InvoiceDetailsDialog from '../../components/InvoiceDetailsDialog';
import QuoteDetailsDialog from '../../components/QuoteDetailsDialog';
import api from '../../services/api';
import { normalizePricingRow } from '../../utils/pricing';
import { downloadQuotePdf } from '../../utils/quotePdf';
import { formatQuoteName, suggestQuoteName } from '../../utils/quoteDisplay';
import { applyQuoteMarginsToPricing, calculateQuoteEstimate } from '../../utils/quotePricing';

const downloadInvoicePdf = async (invoice) => {
  if (!invoice?.id) return;
  try {
    const response = await api.get(`/invoices/${invoice.id}/download_pdf/`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${invoice.invoice_number || invoice.id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading invoice PDF:', error);
    alert('Failed to download invoice PDF. Please try again.');
  }
};

const ManagerCustomers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [apiError, setApiError] = useState('');
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesMessage, setNotesMessage] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [profileForm, setProfileForm] = useState({
    company_name: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [accountActive, setAccountActive] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '', role: '' });
  const [contactMessage, setContactMessage] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState(null);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteData, setQuoteData] = useState({
    quote_name: '',
    diesel_quantity: '',
    def_quantity: '',
    dyed_quantity: '',
    diesel_margin: '',
    def_margin: '',
    dyed_margin: '',
    frequency: 'one-time',
    delivery_fee: '',
    delivery_address: '',
    expires_at: '',
  });
  const [quoteMessage, setQuoteMessage] = useState('');
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [quotePdfFile, setQuotePdfFile] = useState(null);
  const [uploadingQuotePdfId, setUploadingQuotePdfId] = useState(null);
  const [quoteUploadTargetId, setQuoteUploadTargetId] = useState(null);
  const uploadQuotePdfInputRef = useRef(null);
  const createQuotePdfInputRef = useRef(null);
  const [uploadQuoteDialogOpen, setUploadQuoteDialogOpen] = useState(false);
  const [standaloneQuotePdfFile, setStandaloneQuotePdfFile] = useState(null);
  const [standaloneQuoteData, setStandaloneQuoteData] = useState({
    quote_name: '',
    delivery_address: '',
    expires_at: '',
  });
  const [uploadingStandaloneQuote, setUploadingStandaloneQuote] = useState(false);
  const [uploadQuoteMessage, setUploadQuoteMessage] = useState('');
  const [currentPricing, setCurrentPricing] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [createCustomerDialogOpen, setCreateCustomerDialogOpen] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [createCustomerError, setCreateCustomerError] = useState('');
  const [createCustomerSuccess, setCreateCustomerSuccess] = useState('');
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [invoiceDetailDialogOpen, setInvoiceDetailDialogOpen] = useState(false);
  const [selectedInvoiceForDetails, setSelectedInvoiceForDetails] = useState(null);
  const [quoteDetailDialogOpen, setQuoteDetailDialogOpen] = useState(false);
  const [selectedQuoteForDetails, setSelectedQuoteForDetails] = useState(null);
  const [deletingQuoteId, setDeletingQuoteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFormData, setCustomerFormData] = useState({
    email: '',
    company_name: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setApiError('');
      const response = await api.get('/managers/customers/');

      let customersData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          customersData = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          customersData = response.data.results;
        } else if (typeof response.data === 'object') {
          customersData = response.data.data || response.data.customers || [];
        }
      }

      setCustomers(customersData);
    } catch (error) {
      console.error('Error fetching customers:', error);
      console.error('Error response:', error.response);
      setCustomers([]);
      const errorMsg = error.response?.data?.detail || 
                      error.response?.data?.error || 
                      error.message ||
                      'Failed to load customers';
      setApiError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return customers.filter((customer) => {
      const isActive = customer?.is_active !== false;
      if (statusFilter === 'active' && !isActive) return false;
      if (statusFilter === 'inactive' && isActive) return false;
      if (!query) return true;
      const haystack = [
        customer?.company_name,
        customer?.email,
        customer?.user?.email,
        customer?.phone_number,
        customer?.address,
        customer?.city,
        customer?.state,
        customer?.zip_code,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [customers, searchQuery, statusFilter]);

  const handleViewProfile = async (customer) => {
    setSelectedCustomer(customer);
    setProfileDialogOpen(true);
    setProfileLoading(true);
    setCustomerData(null);
    setTabValue(0);
    setNotes('');
    setNotesMessage('');
    setLoginEmail('');
    setEmailMessage('');
    setReminderMessage('');
    
    try {
      const response = await api.get(`/managers/customer-detail/?customer_id=${customer.id}`);
      setCustomerData(response.data);
      setNotes(response.data.profile?.notes || '');
      setLoginEmail(response.data.profile?.email || response.data.profile?.user?.email || '');
      setProfileForm({
        company_name: response.data.profile?.company_name || '',
        phone_number: response.data.profile?.phone_number || '',
        address: response.data.profile?.address || '',
        city: response.data.profile?.city || '',
        state: response.data.profile?.state || '',
        zip_code: response.data.profile?.zip_code || '',
      });
      setProfileMessage('');
      setAccountActive(
        response.data.profile?.is_active ??
          response.data.profile?.user?.is_active ??
          true
      );
      setStatusMessage('');
      setApiError(''); // Clear any previous errors
    } catch (error) {
      console.error('Error fetching customer details:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.message || 
                      'Failed to load customer details';
      setApiError(errorMsg);
      setCustomerData(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!selectedCustomer?.id) return;

    const trimmed = loginEmail.trim();
    if (!trimmed) {
      setEmailMessage('Email is required');
      return;
    }

    setSavingEmail(true);
    setEmailMessage('');

    try {
      const response = await api.put('/managers/customer-email/', {
        customer_id: selectedCustomer.id,
        email: trimmed,
      });
      const updatedEmail = response.data.customer?.email || trimmed;
      setLoginEmail(updatedEmail);
      setEmailMessage('Email updated successfully');
      if (customerData) {
        setCustomerData({
          ...customerData,
          profile: {
            ...customerData.profile,
            email: updatedEmail,
            user: customerData.profile?.user
              ? { ...customerData.profile.user, email: updatedEmail }
              : customerData.profile?.user,
          },
        });
      }
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === selectedCustomer.id ? { ...c, email: updatedEmail } : c
        )
      );
    } catch (error) {
      console.error('Error updating customer email:', error);
      const err = error.response?.data;
      const msg =
        err?.email?.[0] ||
        err?.error ||
        err?.detail ||
        'Failed to update email. Please try again.';
      setEmailMessage(typeof msg === 'string' ? msg : 'Failed to update email. Please try again.');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleProfileFormChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: name === 'state' ? value.toUpperCase() : value,
    }));
  };

  const handleSaveStatus = async () => {
    if (!selectedCustomer?.id) return;

    setSavingStatus(true);
    setStatusMessage('');

    try {
      const response = await api.put('/managers/customer-status/', {
        customer_id: selectedCustomer.id,
        is_active: accountActive,
      });
      const updatedProfile = response.data.customer;
      setStatusMessage(response.data.message || 'Account status updated successfully');
      setCustomerData((prev) => (prev ? { ...prev, profile: updatedProfile } : prev));
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === selectedCustomer.id ? { ...c, is_active: updatedProfile.is_active } : c
        )
      );
    } catch (error) {
      console.error('Error updating customer status:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to update account status. Please try again.';
      setStatusMessage(typeof msg === 'string' ? msg : 'Failed to update account status.');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!selectedCustomer?.id) return;

    setSavingProfile(true);
    setProfileMessage('');

    try {
      const response = await api.put('/managers/customer-profile/', {
        customer_id: selectedCustomer.id,
        ...profileForm,
      });
      const updatedProfile = response.data.customer;
      setProfileMessage('Profile updated successfully');
      setCustomerData((prev) => (prev ? { ...prev, profile: updatedProfile } : prev));
      setProfileForm({
        company_name: updatedProfile.company_name || '',
        phone_number: updatedProfile.phone_number || '',
        address: updatedProfile.address || '',
        city: updatedProfile.city || '',
        state: updatedProfile.state || '',
        zip_code: updatedProfile.zip_code || '',
      });
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === selectedCustomer.id
            ? {
                ...c,
                company_name: updatedProfile.company_name || c.company_name,
                phone_number: updatedProfile.phone_number || c.phone_number,
                address: updatedProfile.address || c.address,
                city: updatedProfile.city || c.city,
                state: updatedProfile.state || c.state,
                zip_code: updatedProfile.zip_code || c.zip_code,
              }
            : c
        )
      );
      setSelectedCustomer((prev) =>
        prev
          ? {
              ...prev,
              company_name: updatedProfile.company_name || prev.company_name,
              phone_number: updatedProfile.phone_number || prev.phone_number,
            }
          : prev
      );
    } catch (error) {
      console.error('Error updating customer profile:', error);
      const err = error.response?.data;
      const firstFieldError = err && typeof err === 'object'
        ? Object.values(err).flat().find((v) => typeof v === 'string')
        : null;
      const msg =
        firstFieldError ||
        err?.error ||
        err?.detail ||
        'Failed to update profile. Please try again.';
      setProfileMessage(typeof msg === 'string' ? msg : 'Failed to update profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedCustomer?.id) return;
    
    setSavingNotes(true);
    setNotesMessage('');
    
    try {
      await api.put('/managers/customer-notes/', {
        customer_id: selectedCustomer.id,
        notes: notes,
      });
      setNotesMessage('Notes saved successfully');
      // Update the customer data
      if (customerData) {
        setCustomerData({
          ...customerData,
          profile: {
            ...customerData.profile,
            notes: notes,
          },
        });
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      setNotesMessage('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleCloseProfileDialog = () => {
    setProfileDialogOpen(false);
    setSelectedCustomer(null);
    setCustomerData(null);
    setTabValue(0);
    setReminderMessage('');
    setProfileMessage('');
    setStatusMessage('');
    setContactMessage('');
    setContactDialogOpen(false);
    setEditingContact(null);
  };

  const handleOpenAddContact = () => {
    setEditingContact(null);
    setContactForm({ name: '', phone: '', email: '', role: '' });
    setContactMessage('');
    setContactDialogOpen(true);
  };

  const handleOpenEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      name: contact.name || '',
      phone: contact.phone || '',
      email: contact.email || '',
      role: contact.role || '',
    });
    setContactMessage('');
    setContactDialogOpen(true);
  };

  const handleContactFormChange = (event) => {
    const { name, value } = event.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveContact = async () => {
    if (!selectedCustomer?.id) return;
    const name = contactForm.name.trim();
    if (!name) {
      setContactMessage('Name is required');
      return;
    }

    setSavingContact(true);
    setContactMessage('');
    try {
      const payload = {
        name,
        phone: contactForm.phone.trim(),
        email: contactForm.email.trim(),
        role: contactForm.role.trim(),
      };
      let response;
      if (editingContact?.id) {
        response = await api.patch('/managers/customer-contacts/', {
          contact_id: editingContact.id,
          ...payload,
        });
      } else {
        response = await api.post('/managers/customer-contacts/', {
          customer_id: selectedCustomer.id,
          ...payload,
        });
      }

      const saved = response.data.contact;
      setCustomerData((prev) => {
        if (!prev) return prev;
        const existing = prev.contacts || [];
        const nextContacts = editingContact?.id
          ? existing.map((c) => (c.id === saved.id ? saved : c))
          : [...existing, saved].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return { ...prev, contacts: nextContacts };
      });
      setContactDialogOpen(false);
      setEditingContact(null);
    } catch (error) {
      console.error('Error saving contact:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.name?.[0] ||
        error.response?.data?.detail ||
        'Failed to save contact';
      setContactMessage(typeof msg === 'string' ? msg : 'Failed to save contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!contact?.id) return;
    if (!window.confirm(`Remove contact "${contact.name}"?`)) return;

    setDeletingContactId(contact.id);
    setContactMessage('');
    try {
      await api.delete('/managers/customer-contacts/', { data: { contact_id: contact.id } });
      setCustomerData((prev) =>
        prev
          ? {
              ...prev,
              contacts: (prev.contacts || []).filter((c) => c.id !== contact.id),
            }
          : prev
      );
    } catch (error) {
      console.error('Error deleting contact:', error);
      setContactMessage(
        error.response?.data?.error || error.response?.data?.detail || 'Failed to delete contact'
      );
    } finally {
      setDeletingContactId(null);
    }
  };

  const handleSendPaymentReminders = async () => {
    if (!selectedCustomer?.id) return;
    setSendingReminders(true);
    setReminderMessage('');
    try {
      const res = await api.post('/managers/send-invoice-payment-reminders/', {
        customer_id: selectedCustomer.id,
      });
      setReminderMessage(
        res.data?.message || `Sent ${res.data?.sent ?? 0} payment reminder(s).`,
      );
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to send payment reminders';
      setReminderMessage(`Failed to send reminders: ${msg}`);
    } finally {
      setSendingReminders(false);
    }
  };

  const handleOpenQuoteDialog = async () => {
    if (selectedCustomer && customerData?.profile) {
      setQuoteData({
        quote_name: suggestQuoteName(selectedCustomer.company_name),
        diesel_quantity: '',
        def_quantity: '',
        dyed_quantity: '',
        diesel_margin: '',
        def_margin: '',
        dyed_margin: '',
        frequency: 'one-time',
        delivery_fee: '',
        delivery_address: customerData.profile.address || '',
        expires_at: '',
      });
      setQuoteMessage('');
      setQuotePdfFile(null);
      setCalculatedPrice(0);
      setQuoteDialogOpen(true);
      
      // Fetch current pricing
      try {
        const response = await api.get('/pricing/pricing/current/');
        setCurrentPricing(normalizePricingRow(response.data));
      } catch (error) {
        console.error('Error fetching current pricing:', error);
        setCurrentPricing(null);
      }
    }
  };

  const handleOpenUploadQuoteDialog = () => {
    const profile = customerData?.profile;
    const addressParts = [
      profile?.address,
      profile?.city,
      profile?.state,
      profile?.zip_code,
    ].filter(Boolean);
    setStandaloneQuoteData({
      quote_name: suggestQuoteName(selectedCustomer?.company_name),
      delivery_address: addressParts.join(', ') || profile?.address || '',
      expires_at: '',
    });
    setStandaloneQuotePdfFile(null);
    setUploadQuoteMessage('');
    setUploadQuoteDialogOpen(true);
  };

  const handleCloseUploadQuoteDialog = () => {
    setUploadQuoteDialogOpen(false);
    setStandaloneQuotePdfFile(null);
    setStandaloneQuoteData({ quote_name: '', delivery_address: '', expires_at: '' });
    setUploadQuoteMessage('');
  };

  const handleStandaloneQuotePdfChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setStandaloneQuotePdfFile(null);
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setUploadQuoteMessage('Please select a PDF file.');
      event.target.value = '';
      setStandaloneQuotePdfFile(null);
      return;
    }
    setStandaloneQuotePdfFile(file);
    setUploadQuoteMessage('');
  };

  const handleUploadStandaloneQuotePdf = async () => {
    if (!selectedCustomer?.id) return;
    if (!standaloneQuotePdfFile) {
      setUploadQuoteMessage('Choose a PDF quote to upload.');
      return;
    }

    setUploadingStandaloneQuote(true);
    setUploadQuoteMessage('');
    try {
      const formData = new FormData();
      formData.append('customer_id', selectedCustomer.id);
      formData.append('pdf_file', standaloneQuotePdfFile);
      if (standaloneQuoteData.quote_name.trim()) {
        formData.append('quote_name', standaloneQuoteData.quote_name.trim());
      }
      if (standaloneQuoteData.delivery_address.trim()) {
        formData.append('delivery_address', standaloneQuoteData.delivery_address.trim());
      }
      if (standaloneQuoteData.expires_at) {
        formData.append('expires_at', standaloneQuoteData.expires_at);
      }
      await api.post('/managers/upload-customer-quote-pdf/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadQuoteMessage('Quote PDF uploaded successfully.');
      await refreshCustomerQuotes();
      setTabValue(3);
      setTimeout(() => {
        handleCloseUploadQuoteDialog();
      }, 1200);
    } catch (error) {
      console.error('Error uploading standalone quote PDF:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to upload quote PDF. Please try again.';
      setUploadQuoteMessage(msg);
    } finally {
      setUploadingStandaloneQuote(false);
    }
  };

  const handleCloseQuoteDialog = () => {
    setQuoteDialogOpen(false);
    setQuoteData({
      quote_name: '',
      diesel_quantity: '',
      def_quantity: '',
      dyed_quantity: '',
      diesel_margin: '',
      def_margin: '',
      dyed_margin: '',
      frequency: 'one-time',
      delivery_fee: '',
      delivery_address: '',
      expires_at: '',
    });
    setQuoteMessage('');
    setQuotePdfFile(null);
    setCalculatedPrice(0);
    setCurrentPricing(null);
  };

  const refreshCustomerQuotes = async () => {
    if (!selectedCustomer?.id) return;
    try {
      const response = await api.get(`/managers/customer-detail/?customer_id=${selectedCustomer.id}`);
      setCustomerData((prev) => (prev ? { ...prev, quotes: response.data.quotes } : response.data));
    } catch (error) {
      console.error('Error refreshing customer quotes:', error);
    }
  };

  const handleCreateQuotePdfChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setQuotePdfFile(null);
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setQuoteMessage('Please select a PDF file.');
      event.target.value = '';
      setQuotePdfFile(null);
      return;
    }
    setQuotePdfFile(file);
    setQuoteMessage('');
  };

  const handleUploadQuotePdf = async (quoteId, file) => {
    if (!quoteId || !file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Please select a PDF file.');
      return;
    }

    setUploadingQuotePdfId(quoteId);
    try {
      const formData = new FormData();
      formData.append('quote_id', quoteId);
      formData.append('pdf_file', file);
      await api.post('/managers/upload-quote-pdf/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshCustomerQuotes();
    } catch (error) {
      console.error('Error uploading quote PDF:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Failed to upload quote PDF. Please try again.';
      alert(msg);
    } finally {
      setUploadingQuotePdfId(null);
      if (uploadQuotePdfInputRef.current) {
        uploadQuotePdfInputRef.current.value = '';
      }
    }
  };

  const handleCreateQuote = async () => {
    if (!selectedCustomer?.id) return;
    
    setCreatingQuote(true);
    setQuoteMessage('');
    
    try {
      const payload = {
        customer: selectedCustomer.id,
        quote_name: quoteData.quote_name.trim(),
        diesel_quantity: parseFloat(quoteData.diesel_quantity) || 0,
        def_quantity: parseFloat(quoteData.def_quantity) || 0,
        dyed_quantity: parseFloat(quoteData.dyed_quantity) || 0,
        diesel_margin: parseFloat(quoteData.diesel_margin) || 0,
        def_margin: parseFloat(quoteData.def_margin) || 0,
        dyed_margin: parseFloat(quoteData.dyed_margin) || 0,
        frequency: quoteData.frequency,
        delivery_fee: parseFloat(quoteData.delivery_fee) || 0,
        delivery_address: quoteData.delivery_address,
        expires_at: quoteData.expires_at || null,
      };

      if (quotePdfFile) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            formData.append(key, value);
          }
        });
        formData.append('pdf_file', quotePdfFile);
        await api.post('/managers/create-quote/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/managers/create-quote/', payload);
      }
      
      setQuoteMessage('Quote created successfully!');
      await refreshCustomerQuotes();
      setTabValue(3);
      setTimeout(() => {
        handleCloseQuoteDialog();
        setQuoteMessage('');
      }, 1500);
    } catch (error) {
      console.error('Error creating quote:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Failed to create quote. Please check your input.';
      setQuoteMessage(errorMsg);
    } finally {
      setCreatingQuote(false);
    }
  };

  useEffect(() => {
    if (currentPricing) {
      setCalculatedPrice(calculateQuoteEstimate(currentPricing, quoteData));
    } else {
      setCalculatedPrice(0);
    }
  }, [
    quoteData.diesel_quantity,
    quoteData.def_quantity,
    quoteData.dyed_quantity,
    quoteData.diesel_margin,
    quoteData.def_margin,
    quoteData.dyed_margin,
    quoteData.delivery_fee,
    currentPricing,
  ]);

  const handleOpenInvoiceDetails = (invoice) => {
    setSelectedInvoiceForDetails(invoice);
    setInvoiceDetailDialogOpen(true);
  };

  const handleCloseInvoiceDetails = () => {
    setInvoiceDetailDialogOpen(false);
    setSelectedInvoiceForDetails(null);
  };

  const handleOpenQuoteDetails = (quote) => {
    setSelectedQuoteForDetails(quote);
    setQuoteDetailDialogOpen(true);
  };

  const handleCloseQuoteDetails = () => {
    setQuoteDetailDialogOpen(false);
    setSelectedQuoteForDetails(null);
  };

  const handleUploadQuotePdfFromDetails = (quote) => {
    setQuoteUploadTargetId(quote.id);
    uploadQuotePdfInputRef.current?.click();
  };

  const handleDeleteQuote = async (quote) => {
    if (!quote?.id) return;
    const label = formatQuoteName(quote);
    if (!window.confirm(`Delete quote "${label}"? This cannot be undone.`)) {
      return;
    }

    setDeletingQuoteId(quote.id);
    try {
      await api.post('/managers/delete-quote/', { quote_id: quote.id });
      if (selectedQuoteForDetails?.id === quote.id) {
        handleCloseQuoteDetails();
      }
      await refreshCustomerQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to delete quote. Please try again.'
      );
    } finally {
      setDeletingQuoteId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleCreateCustomer = async () => {
    setCreatingCustomer(true);
    setCreateCustomerError('');
    setCreateCustomerSuccess('');

    // Validate required fields
    if (!customerFormData.email || !customerFormData.company_name || !customerFormData.phone_number || 
        !customerFormData.address || !customerFormData.city || !customerFormData.state || !customerFormData.zip_code) {
      setCreateCustomerError('Please fill in all required fields');
      setCreatingCustomer(false);
      return;
    }

    try {
      const response = await api.post('/managers/create-customer/', customerFormData);
      setCreateCustomerSuccess(
        response.data.email_sent 
          ? 'Customer created successfully! Invitation email has been sent.'
          : 'Customer created successfully, but email could not be sent. Please contact the customer manually.'
      );
      
      // Refresh customers list
      await fetchCustomers();
      
      // Close dialog after 2 seconds
      setTimeout(() => {
        setCreateCustomerDialogOpen(false);
        setCreateCustomerSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error creating customer:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.response?.data?.email?.[0] ||
                      'Failed to create customer. Please try again.';
      setCreateCustomerError(errorMsg);
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleCustomerFormChange = (e) => {
    const { name, value } = e.target;
    setCustomerFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Customers</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateCustomerDialogOpen(true);
              setCustomerFormData({
                email: '',
                company_name: '',
                phone_number: '',
                address: '',
                city: '',
                state: '',
                zip_code: '',
                notes: '',
              });
              setCreateCustomerError('');
              setCreateCustomerSuccess('');
            }}
          >
            Add New Customer
          </Button>
        </Box>
        
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError('')}>
            {apiError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by company, email, phone, or address…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon color="action" sx={{ mr: 1 }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="customer-list-status-filter">Account status</InputLabel>
              <Select
                labelId="customer-list-status-filter"
                label="Account status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All customers</MenuItem>
                <MenuItem value="active">Active only</MenuItem>
                <MenuItem value="inactive">Inactive only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {filteredCustomers.length} of {customers.length} shown
            </Typography>
          </Grid>
        </Grid>

        <TableContainer component={Paper} sx={{ mt: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!Array.isArray(filteredCustomers) || filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {customers.length === 0
                        ? 'No customers found'
                        : 'No customers match your search or filter'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers.map((customer) => (
                  <TableRow
                    key={customer?.id || Math.random()}
                    hover
                    sx={{
                      cursor: 'pointer',
                      opacity: customer?.is_active === false ? 0.72 : 1,
                    }}
                    onClick={() => handleViewProfile(customer)}
                  >
                    <TableCell>{customer?.company_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={customer?.is_active === false ? 'Inactive' : 'Active'}
                        color={customer?.is_active === false ? 'default' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{customer?.email || customer?.user?.email || 'N/A'}</TableCell>
                    <TableCell>{customer?.phone_number || 'N/A'}</TableCell>
                    <TableCell>
                      {customer?.address ? (
                        customer.address.length > 50 
                          ? `${customer.address.substring(0, 50)}...` 
                          : customer.address
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell>{formatDate(customer?.created_at)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewProfile(customer);
                        }}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Customer Profile Dialog - Shows profile as customer sees it */}
        <Dialog
          open={profileDialogOpen}
          onClose={handleCloseProfileDialog}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: { height: { xs: '100vh', sm: '90vh' } }
          }}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h5" component="span">
                Manage Customer
                {customerData?.profile?.company_name && ` — ${customerData.profile.company_name}`}
              </Typography>
              {customerData?.profile && (
                <Chip
                  label={customerData.profile.is_active === false ? 'Inactive' : 'Active'}
                  color={customerData.profile.is_active === false ? 'default' : 'success'}
                  size="small"
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {profileLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : customerData ? (
              <Box>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
                  <Tab label="Profile" />
                  <Tab label={`Contacts (${customerData.contacts?.length || 0})`} />
                  <Tab label="Orders" />
                  <Tab label="Invoices" />
                  <Tab label="Quotes" />
                  <Tab label="Reports & Trends" />
                </Tabs>

                {/* Profile Tab */}
                {tabValue === 0 && (
                  <Paper sx={{ p: 3 }}>
                    {customerData.profile && (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                          Profile Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Company information shown to the customer in their portal.
                        </Typography>
                        {profileMessage && (
                          <Alert
                            severity={profileMessage.includes('success') ? 'success' : 'error'}
                            sx={{ mb: 2 }}
                            onClose={() => setProfileMessage('')}
                          >
                            {profileMessage}
                          </Alert>
                        )}
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Company Name"
                              name="company_name"
                              value={profileForm.company_name}
                              onChange={handleProfileFormChange}
                              required
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                              name="phone_number"
                              value={profileForm.phone_number}
                              onChange={handleProfileFormChange}
                              required
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Street Address"
                              name="address"
                              value={profileForm.address}
                              onChange={handleProfileFormChange}
                              multiline
                              rows={2}
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="City"
                              name="city"
                              value={profileForm.city}
                              onChange={handleProfileFormChange}
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="State"
                              name="state"
                              value={profileForm.state}
                              onChange={handleProfileFormChange}
                              inputProps={{ maxLength: 2 }}
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              label="Zip Code"
                              name="zip_code"
                              value={profileForm.zip_code}
                              onChange={handleProfileFormChange}
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Registered: {formatDate(customerData.profile.created_at)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Last updated: {formatDate(customerData.profile.updated_at)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              onClick={handleSaveProfile}
                              disabled={savingProfile}
                            >
                              {savingProfile ? 'Saving...' : 'Save Profile'}
                            </Button>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                          Account Status
                        </Typography>
                        {statusMessage && (
                          <Alert
                            severity={statusMessage.startsWith('Failed') ? 'error' : 'success'}
                            sx={{ mb: 2 }}
                            onClose={() => setStatusMessage('')}
                          >
                            {statusMessage}
                          </Alert>
                        )}
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={8}>
                            <FormControl fullWidth>
                              <InputLabel id="customer-status-label">Status</InputLabel>
                              <Select
                                labelId="customer-status-label"
                                label="Status"
                                value={accountActive ? 'active' : 'inactive'}
                                onChange={(e) => setAccountActive(e.target.value === 'active')}
                              >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                              </Select>
                            </FormControl>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Inactive customers and their team members cannot sign in to the portal.
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Button
                              variant="contained"
                              onClick={handleSaveStatus}
                              disabled={savingStatus}
                              sx={{ mt: { xs: 0, sm: 1 } }}
                            >
                              {savingStatus ? 'Saving...' : 'Save Status'}
                            </Button>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                          Login Email (Manager Only)
                        </Typography>
                        {emailMessage && (
                          <Alert
                            severity={emailMessage.includes('success') ? 'success' : 'error'}
                            sx={{ mb: 2 }}
                            onClose={() => setEmailMessage('')}
                          >
                            {emailMessage}
                          </Alert>
                        )}
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              fullWidth
                              label="Login Email"
                              type="email"
                              value={loginEmail}
                              onChange={(e) => setLoginEmail(e.target.value)}
                              helperText="Customer uses this address to sign in and receive invoice emails"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Button
                              variant="contained"
                              onClick={handleSaveEmail}
                              disabled={savingEmail}
                              sx={{ mt: { xs: 0, sm: 1 } }}
                            >
                              {savingEmail ? 'Saving...' : 'Save Email'}
                            </Button>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                          Notes & Comments (Manager Only)
                        </Typography>
                        {notesMessage && (
                          <Alert 
                            severity={notesMessage.includes('success') ? 'success' : 'error'} 
                            sx={{ mb: 2 }}
                            onClose={() => setNotesMessage('')}
                          >
                            {notesMessage}
                          </Alert>
                        )}
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Notes/Comments"
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              multiline
                              rows={6}
                              placeholder="Add internal notes and comments about this customer..."
                              helperText="These notes are only visible to managers"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              onClick={handleSaveNotes}
                              disabled={savingNotes}
                            >
                              {savingNotes ? 'Saving...' : 'Save Notes'}
                            </Button>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Paper>
                )}

                {/* Contacts Tab */}
                {tabValue === 1 && (
                  <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                          Contact People
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          People at this company (name, phone, email, and role).
                        </Typography>
                      </Box>
                      <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddContact}>
                        Add contact
                      </Button>
                    </Box>
                    {contactMessage && !contactDialogOpen && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setContactMessage('')}>
                        {contactMessage}
                      </Alert>
                    )}
                    {(customerData.contacts || []).length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Phone</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Role</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerData.contacts.map((contact) => (
                              <TableRow key={contact.id}>
                                <TableCell>{contact.name}</TableCell>
                                <TableCell>{contact.phone || '—'}</TableCell>
                                <TableCell>{contact.email || '—'}</TableCell>
                                <TableCell>{contact.role || '—'}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Edit contact">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenEditContact(contact)}
                                      aria-label="Edit contact"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remove contact">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={deletingContactId === contact.id}
                                      onClick={() => handleDeleteContact(contact)}
                                      aria-label="Delete contact"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No contacts added yet. Use &quot;Add contact&quot; to record people at this company.
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* Orders Tab */}
                {tabValue === 2 && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      Orders ({customerData.jobs?.length || 0})
                    </Typography>
                    {customerData.jobs && customerData.jobs.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Order ID</TableCell>
                              <TableCell>Location</TableCell>
                              <TableCell>Quantity</TableCell>
                              <TableCell>Frequency</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Payment Status</TableCell>
                              <TableCell>Scheduled Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerData.jobs.map((job) => (
                              <TableRow key={job.id}>
                                <TableCell>{job.id?.substring(0, 8) || 'N/A'}</TableCell>
                                <TableCell>{job.job_location || 'N/A'}</TableCell>
                                <TableCell>
                                  {job.diesel_quantity > 0 && `${job.diesel_quantity} gal Diesel`}
                                  {job.diesel_quantity > 0 && job.def_quantity > 0 && ' / '}
                                  {job.def_quantity > 0 && `${job.def_quantity} gal DEF`}
                                  {((job.diesel_quantity > 0 || job.def_quantity > 0) && job.dyed_quantity > 0) && ' / '}
                                  {job.dyed_quantity > 0 && `${job.dyed_quantity} gal Dyed`}
                                  {(!job.diesel_quantity || job.diesel_quantity === 0) && (!job.def_quantity || job.def_quantity === 0) && (!job.dyed_quantity || job.dyed_quantity === 0) && 'N/A'}
                                </TableCell>
                                <TableCell>{job.frequency || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={job.job_status || 'N/A'}
                                    color={
                                      job.job_status === 'complete' ? 'success' :
                                      job.job_status === 'active' ? 'primary' :
                                      job.job_status === 'pending' ? 'warning' : 'default'
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={job.payment_status || 'N/A'}
                                    color={job.payment_status === 'paid' ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{formatDate(job.scheduled_date)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">No orders found for this customer.</Alert>
                    )}
                  </Box>
                )}

                {/* Invoices Tab */}
                {tabValue === 3 && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">
                        Invoices ({customerData.invoices?.length || 0})
                      </Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={sendingReminders ? <CircularProgress size={18} /> : <EmailIcon />}
                        disabled={sendingReminders || !customerData.invoices?.length}
                        onClick={handleSendPaymentReminders}
                      >
                        Send payment reminders
                      </Button>
                    </Box>
                    {reminderMessage && (
                      <Alert
                        severity={
                          reminderMessage.startsWith('Failed to send')
                            ? 'error'
                            : /^Sent 0 payment reminder/.test(reminderMessage)
                              ? 'info'
                              : 'success'
                        }
                        sx={{ mb: 2 }}
                        onClose={() => setReminderMessage('')}
                      >
                        {reminderMessage}
                      </Alert>
                    )}
                    {customerData.invoices && customerData.invoices.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Invoice Number</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell>Created Date</TableCell>
                              <TableCell align="right">Invoice PDF</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerData.invoices.map((invoice) => (
                              <TableRow
                                key={invoice.id}
                                hover
                                onClick={() => handleOpenInvoiceDetails(invoice)}
                                sx={{ cursor: 'pointer' }}
                              >
                                <TableCell>{invoice.invoice_number || 'N/A'}</TableCell>
                                <TableCell>
                                  ${invoice.total_amount ? parseFloat(invoice.total_amount).toFixed(2) : '0.00'}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={invoice.status || 'N/A'}
                                    color={
                                      invoice.status === 'paid' ? 'success' :
                                      invoice.status === 'submitted' ? 'warning' : 'default'
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{formatDate(invoice.due_date)}</TableCell>
                                <TableCell>{formatDate(invoice.created_at)}</TableCell>
                                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadInvoicePdf(invoice);
                                    }}
                                  >
                                    PDF
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">No invoices found for this customer.</Alert>
                    )}
                  </Box>
                )}

                {/* Quotes Tab */}
                {tabValue === 4 && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">
                        Quotes ({customerData.quotes?.length || 0})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<UploadFileIcon />}
                          onClick={handleOpenUploadQuoteDialog}
                        >
                          Upload Quote PDF
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleOpenQuoteDialog}
                        >
                          Create Quote
                        </Button>
                      </Box>
                    </Box>
                    {customerData.quotes && customerData.quotes.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Diesel Quantity</TableCell>
                              <TableCell>DEF Quantity</TableCell>
                              <TableCell>Dyed Quantity</TableCell>
                              <TableCell>Frequency</TableCell>
                              <TableCell>Estimated Price</TableCell>
                              <TableCell>Delivery Fee</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Expires At</TableCell>
                              <TableCell>Created Date</TableCell>
                              <TableCell align="right">Quote PDF</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerData.quotes.map((quote) => (
                              <TableRow
                                key={quote.id}
                                hover
                                onClick={() => handleOpenQuoteDetails(quote)}
                                sx={{ cursor: 'pointer' }}
                              >
                                <TableCell>{formatQuoteName(quote)}</TableCell>
                                <TableCell>{parseFloat(quote.diesel_quantity || 0).toFixed(2)} gal</TableCell>
                                <TableCell>{parseFloat(quote.def_quantity || 0).toFixed(2)} gal</TableCell>
                                <TableCell>{parseFloat(quote.dyed_quantity || 0).toFixed(2)} gal</TableCell>
                                <TableCell>{quote.frequency || 'N/A'}</TableCell>
                                <TableCell>${parseFloat(quote.estimated_price || 0).toFixed(2)}</TableCell>
                                <TableCell>${parseFloat(quote.delivery_fee || 0).toFixed(2)}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                    <Chip
                                      label={quote.status || 'N/A'}
                                      color={
                                        quote.status === 'accepted' ? 'success' :
                                        quote.status === 'submitted' ? 'info' :
                                        quote.status === 'rejected' ? 'error' :
                                        quote.status === 'expired' ? 'warning' : 'default'
                                      }
                                      size="small"
                                    />
                                    {quote.has_uploaded_pdf && (
                                      <Chip label="Uploaded PDF" size="small" variant="outlined" color="secondary" />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>{quote.expires_at ? formatDate(quote.expires_at) : 'N/A'}</TableCell>
                                <TableCell>{formatDate(quote.created_at)}</TableCell>
                                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<PictureAsPdfIcon />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        downloadQuotePdf(quote.id);
                                      }}
                                    >
                                      Download
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                      startIcon={
                                        uploadingQuotePdfId === quote.id ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <UploadFileIcon />
                                        )
                                      }
                                      disabled={uploadingQuotePdfId === quote.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setQuoteUploadTargetId(quote.id);
                                        uploadQuotePdfInputRef.current?.click();
                                      }}
                                    >
                                      {quote.has_uploaded_pdf ? 'Replace' : 'Upload'}
                                    </Button>
                                    <Tooltip title="Delete quote">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          disabled={deletingQuoteId === quote.id}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteQuote(quote);
                                          }}
                                        >
                                          {deletingQuoteId === quote.id ? (
                                            <CircularProgress size={18} />
                                          ) : (
                                            <DeleteIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert
                        severity="info"
                        action={
                          <Button color="inherit" size="small" onClick={handleOpenUploadQuoteDialog}>
                            Upload Quote PDF
                          </Button>
                        }
                      >
                        No quotes yet. Upload a PDF quote prepared elsewhere for this customer, or create one in the app.
                      </Alert>
                    )}
                  </Box>
                )}

                {/* Reports & Trends Tab */}
                {tabValue === 5 && selectedCustomer?.id && (
                  <ManagerReportsSection
                    customerId={selectedCustomer.id}
                    customerName={customerData.profile?.company_name || selectedCustomer.company_name}
                    embedded
                  />
                )}
              </Box>
            ) : (
              <Alert severity="error">Failed to load customer data</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseProfileDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Upload external quote PDF dialog */}
        <Dialog
          open={uploadQuoteDialogOpen}
          onClose={handleCloseUploadQuoteDialog}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>
            Upload Quote PDF for {selectedCustomer?.company_name || 'Customer'}
          </DialogTitle>
          <DialogContent>
            {uploadQuoteMessage && (
              <Alert
                severity={
                  uploadQuoteMessage.includes('success') ? 'success' : 'error'
                }
                sx={{ mb: 2 }}
                onClose={() => setUploadQuoteMessage('')}
              >
                {uploadQuoteMessage}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use this when you already have a quote PDF from another system or document.
              The customer will see it in their quotes list and can download it from the portal.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Quote name"
                  value={standaloneQuoteData.quote_name}
                  onChange={(e) =>
                    setStandaloneQuoteData({
                      ...standaloneQuoteData,
                      quote_name: e.target.value,
                    })
                  }
                  helperText="Shown to the customer instead of an internal quote ID."
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                >
                  {standaloneQuotePdfFile ? 'Change PDF file' : 'Choose PDF file'}
                  <input
                    type="file"
                    hidden
                    accept="application/pdf,.pdf"
                    onChange={handleStandaloneQuotePdfChange}
                  />
                </Button>
                {standaloneQuotePdfFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected: {standaloneQuotePdfFile.name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Delivery address (optional)"
                  multiline
                  rows={2}
                  value={standaloneQuoteData.delivery_address}
                  onChange={(e) =>
                    setStandaloneQuoteData({
                      ...standaloneQuoteData,
                      delivery_address: e.target.value,
                    })
                  }
                  helperText="Defaults to the customer address if left blank."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Expires at (optional)"
                  type="date"
                  value={standaloneQuoteData.expires_at}
                  onChange={(e) =>
                    setStandaloneQuoteData({
                      ...standaloneQuoteData,
                      expires_at: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUploadQuoteDialog} disabled={uploadingStandaloneQuote}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={uploadingStandaloneQuote ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
              disabled={uploadingStandaloneQuote || !standaloneQuotePdfFile}
              onClick={handleUploadStandaloneQuotePdf}
            >
              {uploadingStandaloneQuote ? 'Uploading...' : 'Upload Quote PDF'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Customer contact dialog */}
        <Dialog
          open={contactDialogOpen}
          onClose={() => {
            setContactDialogOpen(false);
            setEditingContact(null);
            setContactMessage('');
          }}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>{editingContact ? 'Edit contact' : 'Add contact'}</DialogTitle>
          <DialogContent dividers>
            {contactMessage && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setContactMessage('')}>
                {contactMessage}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                name="name"
                label="Name"
                value={contactForm.name}
                onChange={handleContactFormChange}
                required
                fullWidth
              />
              <TextField
                name="phone"
                label="Phone"
                value={contactForm.phone}
                onChange={handleContactFormChange}
                fullWidth
              />
              <TextField
                name="email"
                label="Email"
                type="email"
                value={contactForm.email}
                onChange={handleContactFormChange}
                fullWidth
              />
              <TextField
                name="role"
                label="Role in company"
                value={contactForm.role}
                onChange={handleContactFormChange}
                fullWidth
                placeholder="e.g. Fleet Manager, Accounts Payable"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setContactDialogOpen(false);
                setEditingContact(null);
                setContactMessage('');
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveContact} disabled={savingContact}>
              {savingContact ? 'Saving…' : editingContact ? 'Save changes' : 'Add contact'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Quote Dialog */}
        <Dialog
          open={quoteDialogOpen}
          onClose={handleCloseQuoteDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Create Quote for {selectedCustomer?.company_name || 'Customer'}</DialogTitle>
          <DialogContent>
            {quoteMessage && (
              <Alert 
                severity={quoteMessage.includes('success') ? 'success' : 'error'} 
                sx={{ mb: 2 }}
                onClose={() => setQuoteMessage('')}
              >
                {quoteMessage}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Quote name"
                  value={quoteData.quote_name}
                  onChange={(e) => setQuoteData({ ...quoteData, quote_name: e.target.value })}
                  helperText="Shown to the customer instead of an internal quote ID."
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Diesel quantity (gallons)"
                  type="number"
                  value={quoteData.diesel_quantity}
                  onChange={(e) => setQuoteData({ ...quoteData, diesel_quantity: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="DEF quantity (gallons)"
                  type="number"
                  value={quoteData.def_quantity}
                  onChange={(e) => setQuoteData({ ...quoteData, def_quantity: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Dyed quantity (gallons)"
                  type="number"
                  value={quoteData.dyed_quantity}
                  onChange={(e) => setQuoteData({ ...quoteData, dyed_quantity: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Diesel margin ($/gal)"
                  type="number"
                  value={quoteData.diesel_margin}
                  onChange={(e) => setQuoteData({ ...quoteData, diesel_margin: e.target.value })}
                  inputProps={{ step: '0.0001', min: '0' }}
                  helperText="Added to base diesel price on the quote"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="DEF margin ($/gal)"
                  type="number"
                  value={quoteData.def_margin}
                  onChange={(e) => setQuoteData({ ...quoteData, def_margin: e.target.value })}
                  inputProps={{ step: '0.0001', min: '0' }}
                  helperText="Added to base DEF price on the quote"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Dyed margin ($/gal)"
                  type="number"
                  value={quoteData.dyed_margin}
                  onChange={(e) => setQuoteData({ ...quoteData, dyed_margin: e.target.value })}
                  inputProps={{ step: '0.0001', min: '0' }}
                  helperText="Added to base dyed price on the quote"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={quoteData.frequency}
                    onChange={(e) => setQuoteData({ ...quoteData, frequency: e.target.value })}
                    label="Frequency"
                  >
                    <MenuItem value="one-time">One-time</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {currentPricing && (() => {
                const billedPricing = applyQuoteMarginsToPricing(currentPricing, quoteData);
                const hasMargin =
                  parseFloat(quoteData.diesel_margin) > 0 ||
                  parseFloat(quoteData.def_margin) > 0 ||
                  parseFloat(quoteData.dyed_margin) > 0;
                return (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {hasMargin ? 'Quoted unit prices (base + margin)' : 'Current Pricing'}
                    </Typography>
                    <Typography variant="body2">
                      Diesel: ${billedPricing.diesel_price_per_liter}/gal 
                      {currentPricing.diesel_sales_tax > 0 && ` (${(parseFloat(currentPricing.diesel_sales_tax) * 100).toFixed(2)}% tax)`}
                      {' | '}
                      DEF: ${billedPricing.def_price_per_liter}/gal
                      {currentPricing.def_sales_tax > 0 && ` (${(parseFloat(currentPricing.def_sales_tax) * 100).toFixed(2)}% tax)`}
                      {' | '}
                      Dyed: ${billedPricing.dyed_price_per_liter}/gal
                      {currentPricing.dyed_sales_tax > 0 && ` (${(parseFloat(currentPricing.dyed_sales_tax) * 100).toFixed(2)}% tax)`}
                    </Typography>
                  </Paper>
                </Grid>
                );
              })()}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Delivery Fee ($)"
                  type="number"
                  value={quoteData.delivery_fee}
                  onChange={(e) => setQuoteData({ ...quoteData, delivery_fee: e.target.value })}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Estimated Price
                  </Typography>
                  <Typography variant="h6">
                    ${calculatedPrice.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    (Calculated from current prices)
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Delivery Address"
                  multiline
                  rows={3}
                  value={quoteData.delivery_address}
                  onChange={(e) => setQuoteData({ ...quoteData, delivery_address: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Expires At (Optional)"
                  type="date"
                  value={quoteData.expires_at}
                  onChange={(e) => setQuoteData({ ...quoteData, expires_at: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Quote PDF (optional)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                  >
                    {quotePdfFile ? 'Change PDF' : 'Choose PDF'}
                    <input
                      ref={createQuotePdfInputRef}
                      type="file"
                      hidden
                      accept="application/pdf,.pdf"
                      onChange={handleCreateQuotePdfChange}
                    />
                  </Button>
                  {quotePdfFile && (
                    <Typography variant="body2" color="text.secondary">
                      {quotePdfFile.name}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Upload a custom PDF for the customer. If omitted, a PDF is generated from quote details.
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseQuoteDialog} disabled={creatingQuote}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateQuote}
              variant="contained"
              disabled={
                creatingQuote ||
                !quoteData.quote_name.trim() ||
                !quoteData.delivery_address ||
                (
                  !quotePdfFile &&
                  ((!quoteData.diesel_quantity && !quoteData.def_quantity && !quoteData.dyed_quantity) || !currentPricing)
                )
              }
            >
              {creatingQuote ? 'Creating...' : 'Create Quote'}
            </Button>
          </DialogActions>
        </Dialog>

        <input
          ref={uploadQuotePdfInputRef}
          type="file"
          hidden
          accept="application/pdf,.pdf"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (quoteUploadTargetId && file) {
              handleUploadQuotePdf(quoteUploadTargetId, file);
            }
            e.target.value = '';
            setQuoteUploadTargetId(null);
          }}
        />

        {/* Create Customer Dialog */}
        <Dialog
          open={createCustomerDialogOpen}
          onClose={() => !creatingCustomer && setCreateCustomerDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Add New Customer</DialogTitle>
          <DialogContent dividers>
            {createCustomerError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateCustomerError('')}>
                {createCustomerError}
              </Alert>
            )}
            {createCustomerSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateCustomerSuccess('')}>
                {createCustomerSuccess}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label="Email *"
                  type="email"
                  value={customerFormData.email}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                  helperText="Customer will receive an invitation email to create their account"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="company_name"
                  label="Company Name *"
                  value={customerFormData.company_name}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="phone_number"
                  label="Phone Number *"
                  value={customerFormData.phone_number}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="address"
                  label="Street Address *"
                  value={customerFormData.address}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="city"
                  label="City *"
                  value={customerFormData.city}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="state"
                  label="State *"
                  value={customerFormData.state}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                  inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                  helperText="2-letter code (e.g., CA)"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  name="zip_code"
                  label="ZIP Code *"
                  value={customerFormData.zip_code}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notes (Optional)"
                  value={customerFormData.notes}
                  onChange={handleCustomerFormChange}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Internal notes about this customer"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setCreateCustomerDialogOpen(false)} 
              disabled={creatingCustomer}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCustomer}
              variant="contained"
              color="primary"
              disabled={creatingCustomer}
            >
              {creatingCustomer ? 'Creating...' : 'Create Customer'}
            </Button>
          </DialogActions>
        </Dialog>

        <InvoiceDetailsDialog
          open={invoiceDetailDialogOpen}
          onClose={handleCloseInvoiceDetails}
          invoice={selectedInvoiceForDetails}
          showCustomer
          isMobile={isMobile}
        />

        <QuoteDetailsDialog
          open={quoteDetailDialogOpen}
          onClose={handleCloseQuoteDetails}
          quote={selectedQuoteForDetails}
          showCustomerName
          onUploadPdf={handleUploadQuotePdfFromDetails}
          onDelete={handleDeleteQuote}
          deleting={deletingQuoteId === selectedQuoteForDetails?.id}
          uploadingPdf={uploadingQuotePdfId === selectedQuoteForDetails?.id}
          fullScreen={isMobile}
        />
      </Container>
    </Layout>
  );
};

export default ManagerCustomers;

